/**
 * User actions — one implementation for every surface that acts on a user:
 * Admin → Users, Company → Members and the user page. Owns the dialogs and
 * mutations; the surface renders `dialogs` once and hands `actions` to a
 * `UserActionsMenu` or its own buttons.
 *
 * Given a `companyId` the surface is acting from that company, so the
 * destructive action is Remove from company (the user's membership there) and
 * account Delete is not offered at all (owner decision 2026-10-26).
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@lognai/synapse-design-system-v2';
import type { User } from '@lognai/core-platform';
import {
  useCompany,
  useDeleteUser,
  useRemoveUserFromCompany,
  useUpdateUserStatus,
} from '../../hooks';
import { UserStatus } from '../../types/domain-enums';
import { ConfirmDialog } from '../shared';
import { ResetPasswordModal } from './ResetPasswordModal';
import { UserEditModal } from './UserEditModal';
import type { UserActionCallbacks } from './UserActionsMenu';

export interface UseUserActionsOptions {
  /** The company the surface acts from; switches Delete to Remove from company. */
  companyId?: string;
  onDeleted?: (user: User) => void;
  onRemovedFromCompany?: (user: User) => void;
}

export interface UseUserActionsResult {
  actions: UserActionCallbacks;
  /** The edit / password-link / confirm dialogs; render once. */
  dialogs: React.ReactNode;
}

const labelOf = (user: User) => user.fullName || user.email;
const errorText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback);

/**
 * Confirm copy for Remove from company. When it is the user's primary company
 * the backend promotes another company only if they are a FULL member there
 * (an active membership, not a partner/guest link) — the UI can't see that
 * cheaply, so with other companies on the account the line stays neutral.
 */
export function removeFromCompanyCopy(user: User, companyId: string, companyName: string): string {
  const parts = [
    `Remove ${labelOf(user)} from ${companyName}? Their account and any other companies stay as they are.`,
  ];
  if (user.companyId === companyId) {
    const hasOtherCompanies = !!user.secondaryCompanyIds?.some((id) => id !== companyId);
    parts.push(
      hasOtherCompanies
        ? "This is their primary company. If they're a full member of another company it becomes their primary; otherwise they won't be able to sign in until they're added to one."
        : "This is their only company — they won't be able to sign in until they're added to another."
    );
  }
  parts.push("They'll be signed out.");
  return parts.join(' ');
}

export function useUserActions({
  companyId,
  onDeleted,
  onRemovedFromCompany,
}: UseUserActionsOptions = {}): UseUserActionsResult {
  const navigate = useNavigate();
  const { toast } = useToast();
  const updateStatus = useUpdateUserStatus();
  const deleteUser = useDeleteUser();
  const removeFromCompany = useRemoveUserFromCompany(companyId ?? '');
  const { data: company } = useCompany(companyId ?? '');
  const companyName = company?.name || 'this company';

  const [editing, setEditing] = useState<User | null>(null);
  const [passwordLinkFor, setPasswordLinkFor] = useState<User | null>(null);
  const [deleting, setDeleting] = useState<User | null>(null);
  const [removing, setRemoving] = useState<User | null>(null);

  const companyQuery = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';

  const setStatus = async (user: User, status: UserStatus, verb: 'activated' | 'suspended') => {
    try {
      await updateStatus.mutateAsync({ userId: user.id, status });
      toast({ title: `User ${verb}`, description: `${user.email} has been ${verb}` });
    } catch (err) {
      toast({
        title: 'Error',
        description: errorText(err, `Failed to ${verb === 'activated' ? 'activate' : 'suspend'} user`),
        variant: 'destructive',
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const user = deleting;
    try {
      await deleteUser.mutateAsync(user.id);
      toast({ title: 'User deleted', description: `Successfully deleted ${user.email}` });
      setDeleting(null);
      onDeleted?.(user);
    } catch (err) {
      toast({ title: 'Error', description: errorText(err, 'Failed to delete user'), variant: 'destructive' });
    }
  };

  const confirmRemove = async () => {
    if (!removing) return;
    const user = removing;
    try {
      await removeFromCompany.mutateAsync(user.id);
      toast({
        title: 'Removed from company',
        description: `${labelOf(user)} was removed from ${companyName}.`,
      });
      setRemoving(null);
      onRemovedFromCompany?.(user);
    } catch (err) {
      toast({
        title: 'Error',
        description: errorText(err, 'Failed to remove the user from this company'),
        variant: 'destructive',
      });
    }
  };

  const actions: UserActionCallbacks = {
    onEditUser: setEditing,
    onResetPassword: setPasswordLinkFor,
    onManageRoles: (user) => navigate(`/admin/users/${user.id}/roles${companyQuery}`),
    onManageAccess: (user) => navigate(`/admin/users/${user.id}/access${companyQuery}`),
    onActivateUser: (user) => setStatus(user, UserStatus.ACTIVE, 'activated'),
    onSuspendUser: (user) => setStatus(user, UserStatus.SUSPENDED, 'suspended'),
    ...(companyId ? { onRemoveFromCompany: setRemoving } : { onDeleteUser: setDeleting }),
  };

  const dialogs = (
    <>
      {editing && (
        <UserEditModal
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          user={editing}
        />
      )}

      {passwordLinkFor && (
        <ResetPasswordModal
          open
          onOpenChange={(open) => {
            if (!open) setPasswordLinkFor(null);
          }}
          user={passwordLinkFor}
        />
      )}

      {!companyId && (
        <ConfirmDialog
          open={deleting !== null}
          onOpenChange={(open) => {
            if (!open) setDeleting(null);
          }}
          title="Delete User"
          description={
            deleting
              ? `Are you sure you want to delete ${deleting.email}? This action cannot be undone.`
              : ''
          }
          confirmButtonText="Delete"
          confirmVariant="destructive"
          isLoading={deleteUser.isPending}
          onConfirm={confirmDelete}
        />
      )}

      {companyId && (
        <ConfirmDialog
          open={removing !== null}
          onOpenChange={(open) => {
            if (!open) setRemoving(null);
          }}
          title="Remove from company"
          description={removing ? removeFromCompanyCopy(removing, companyId, companyName) : ''}
          confirmButtonText="Remove"
          confirmVariant="destructive"
          isLoading={removeFromCompany.isPending}
          onConfirm={confirmRemove}
        />
      )}
    </>
  );

  return { actions, dialogs };
}
